import React, { useState } from 'react';
import { Sparkles, Loader2, ArrowRight, X, Check, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWebOrigin } from '../../utils/helpers';
import { renderChatMessage } from '../../utils/renderChatMessage';
import { TOURNAMENT_FORMATS } from '../registry/formatRegistry';

const HELP_API = getWebOrigin() + '/helpapi';
const HELP_APP_ID = import.meta.env.REACT_APP_HELP_APP_ID;

// Picks whichever known format label appears EARLIEST in the reply — the
// chat agent tends to lead with its primary recommendation, so the first
// label mentioned is a reasonable proxy for "the one it's suggesting" even
// though the reply is free text, not a structured pick.
const findMentionedFormat = (text) => {
  if (!text) return null;
  let best = null;
  for (const format of TOURNAMENT_FORMATS) {
    const idx = text.toLowerCase().indexOf(format.label.toLowerCase());
    if (idx !== -1 && (best === null || idx < best.idx)) {
      best = { idx, format };
    }
  }
  return best?.format ?? null;
};

/**
 * Free-text "which format fits my need" helper on the format-selection step.
 * Calls the same /helpapi/chat backend as the global Kyna widget (already
 * routes format-choice questions to tournament-formats-guide.html — see
 * app_configs.py in kynaix-chat-agent) rather than standing up anything new.
 */
const AskAIFormatHelper = ({ participantCount, sportName, onSelectFormat, eligibleFormats }) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const ask = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setLoading(true);
    setError(null);
    setReply(null);
    setSelectedId(null);

    const contextBits = [
      participantCount ? `${participantCount} participants` : null,
      sportName ? `sport: ${sportName}` : null,
    ].filter(Boolean).join(', ');
    const message = contextBits
      ? `${text}\n\n(Context: choosing a tournament format on Kynaix Sports. ${contextBits}.)`
      : text;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${HELP_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          history: [],
          current_page: window.location.pathname,
          user_token: token || undefined,
          app_id: HELP_APP_ID,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReply(data.reply);
    } catch (err) {
      setError('Something went wrong asking AI — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const matched = reply ? findMentionedFormat(reply) : null;
  // AI's suggestion is free text, not aware of the hard participant-count/sport
  // rules FormatPicker's cards already enforce (formatEligibility.js) — a
  // format it names can still be one the current count doesn't qualify for
  // (e.g. suggesting Group Stage + Knockout at 4 participants when it needs
  // 8+). Same eligibility map the cards below already read from, so the
  // reason text matches exactly what a disabled card would show.
  const matchedEligibility = matched ? eligibleFormats?.[matched.id] : null;
  const matchedIneligible = matched && matchedEligibility && !matchedEligibility.enabled;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`mb-4 flex w-full items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${theme.card} ${theme.cardHover}`}
      >
        <span className="flex items-center gap-3">
          <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${theme.buttonPrimary}`}>
            <Sparkles size={16} />
          </span>
          <span>
            <span className={`block text-sm font-semibold ${theme.text}`}>Not sure which format fits?</span>
            <span className={`block text-xs ${theme.textMuted}`}>Describe what you need and let AI suggest one</span>
          </span>
        </span>
        <ArrowRight size={16} className={`flex-shrink-0 ${theme.textMuted}`} />
      </button>
    );
  }

  return (
    <div className={`mb-4 rounded-2xl border p-4 ${theme.card}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <Sparkles size={16} className={theme.link} />
          <span className={`text-sm font-semibold ${theme.text}`}>Ask AI which format fits</span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className={`rounded-lg p-1 transition-colors ${theme.textMuted} ${theme.cardHover}`}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            ask();
          }
        }}
        placeholder="e.g. I want to compete with friends living in another country"
        rows={2}
        className={`w-full resize-none rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${theme.input}`}
      />

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={ask}
          disabled={!input.trim() || loading}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${theme.buttonPrimary}`}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading ? 'Thinking…' : 'Ask AI'}
        </button>
      </div>

      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      {reply && (
        <div className={`mt-3 rounded-xl p-3 text-sm leading-relaxed ${theme.backgroundSecondary} ${theme.text}`}>
          <div className="whitespace-pre-wrap">{renderChatMessage(reply)}</div>
          {matched && matchedIneligible && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>{matched.label}</strong> isn't usable yet — {matchedEligibility.reason.charAt(0).toLowerCase() + matchedEligibility.reason.slice(1)}.
                Go back and adjust your participant count to use this suggestion, or ask again with your current count in mind.
              </span>
            </div>
          )}
          {matched && !matchedIneligible && (
            <button
              type="button"
              onClick={() => {
                onSelectFormat(matched.id);
                setSelectedId(matched.id);
              }}
              className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                selectedId === matched.id ? `${theme.buttonGhost}` : theme.buttonPrimary
              }`}
            >
              {selectedId === matched.id ? (
                <>
                  <Check size={13} />
                  Selected {matched.label}
                </>
              ) : (
                <>
                  Use {matched.label}
                  <ArrowRight size={13} />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AskAIFormatHelper;
